import { create } from 'zustand'
import type { AnalyticsEvent, AnalyticsSummary } from '../types'
import { getAnalyticsEvents, addAnalyticsEvent as dbAddEvent } from '../lib/api-db'
import { format, subDays, startOfDay, isWithinInterval } from 'date-fns'

interface AnalyticsState {
  events: AnalyticsEvent[]
  isLoading: boolean

  loadAnalytics: (days?: number) => Promise<void>
  trackCopy: (platform: 'twitter' | 'facebook' | 'forum' | 'infographic') => Promise<void>
  trackDownload: (platform: 'infographic') => Promise<void>
  trackTemplateUse: () => Promise<void>
  getSummary: () => AnalyticsSummary
}

function generateId(): string {
  return `evt-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  events: [],
  isLoading: false,

  loadAnalytics: async (days = 30) => {
    set({ isLoading: true })
    try {
      const events = await getAnalyticsEvents(days)
      set({ events, isLoading: false })
    } catch (error) {
      console.error('Failed to load analytics:', error)
      set({ isLoading: false })
    }
  },

  trackCopy: async (platform) => {
    const event: AnalyticsEvent = {
      id: generateId(),
      type: 'copy',
      platform,
      timestamp: new Date(),
    }

    try {
      await dbAddEvent(event)
      set((state) => ({
        events: [...state.events, event],
      }))
    } catch (error) {
      console.error('Failed to track copy:', error)
    }
  },

  trackDownload: async (platform) => {
    const event: AnalyticsEvent = {
      id: generateId(),
      type: 'copy', // Reuse 'copy' type for downloads (similar action)
      platform,
      timestamp: new Date(),
    }

    try {
      await dbAddEvent(event)
      set((state) => ({
        events: [...state.events, event],
      }))
    } catch (error) {
      console.error('Failed to track download:', error)
    }
  },

  trackTemplateUse: async () => {
    const event: AnalyticsEvent = {
      id: generateId(),
      type: 'template_use',
      timestamp: new Date(),
    }

    try {
      await dbAddEvent(event)
      set((state) => ({
        events: [...state.events, event],
      }))
    } catch (error) {
      console.error('Failed to track template use:', error)
    }
  },

  getSummary: () => {
    const { events } = get()

    const now = new Date()
    const weekAgo = subDays(now, 7)

    const queryEvents = events.filter((e) => e.type === 'query')
    const copyEvents = events.filter((e) => e.type === 'copy')

    // Total queries
    const totalQueries = queryEvents.length

    // Queries this week
    const queriesThisWeek = queryEvents.filter((e) =>
      isWithinInterval(new Date(e.timestamp), { start: weekAgo, end: now })
    ).length

    // Average response time
    const responseTimes = queryEvents
      .filter((e) => e.responseTime)
      .map((e) => e.responseTime!)
    const averageResponseTime =
      responseTimes.length > 0
        ? Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length)
        : 0

    // Confidence distribution
    const confidenceDistribution = {
      high: queryEvents.filter((e) => e.confidence === 'high').length,
      medium: queryEvents.filter((e) => e.confidence === 'medium').length,
      low: queryEvents.filter((e) => e.confidence === 'low').length,
    }

    // Copy count by platform
    const copyCountByPlatform = {
      twitter: copyEvents.filter((e) => e.platform === 'twitter').length,
      facebook: copyEvents.filter((e) => e.platform === 'facebook').length,
      forum: copyEvents.filter((e) => e.platform === 'forum').length,
      infographic: copyEvents.filter((e) => e.platform === 'infographic').length,
    }

    // Queries by day (last 7 days)
    const queriesByDay: { date: string; count: number }[] = []
    for (let i = 6; i >= 0; i--) {
      const date = subDays(now, i)
      const dayStart = startOfDay(date)
      const dayEnd = new Date(dayStart)
      dayEnd.setDate(dayEnd.getDate() + 1)

      const count = queryEvents.filter((e) =>
        isWithinInterval(new Date(e.timestamp), { start: dayStart, end: dayEnd })
      ).length

      queriesByDay.push({
        date: format(date, 'MMM d'),
        count,
      })
    }

    return {
      totalQueries,
      queriesThisWeek,
      averageResponseTime,
      confidenceDistribution,
      copyCountByPlatform,
      queriesByDay,
    }
  },
}))
